import { useEffect, useMemo, useState, type ReactElement } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import { downloadInvoiceFile } from '../api/invoicesService'

interface InvoiceFilePreviewProps {
  /** Local file for immediate preview (create dialog) */
  file?: File | null
  /** Remote invoice UUID for fetching file (edit dialog) */
  invoiceUuid?: string
  /** Whether the remote invoice has a file */
  hasFile?: boolean
  /** Original filename — fallback for MIME type detection when Content-Type is generic */
  originalFilename?: string | null
}

function typeFromExtension(filename: string): string {
  const ext = filename.toLowerCase().split('.').pop()
  if (ext === 'pdf') return 'application/pdf'
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg'
  if (ext === 'png') return 'image/png'
  return 'application/octet-stream'
}

function resolveType(file: File): string {
  if (file.type) return file.type
  return typeFromExtension(file.name)
}

function PreviewRenderer({ url, mimeType }: { url: string; mimeType: string }): ReactElement {
  if (mimeType === 'application/pdf') {
    return (
      <iframe
        src={`${url}#toolbar=0`}
        className="w-full h-[300px] sm:h-[70vh] rounded-lg border border-border/40"
        title="Invoice preview"
      />
    )
  }

  if (mimeType.startsWith('image/')) {
    return (
      <div className="flex items-center justify-center h-[300px] sm:h-[70vh] bg-muted/30 rounded-lg overflow-hidden">
        <img
          src={url}
          alt="Invoice preview"
          className="max-w-full max-h-full object-contain"
        />
      </div>
    )
  }

  return <EmptyPreview message="Preview not available" />
}

function EmptyPreview({ message = 'No preview' }: { message?: string }): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center h-[300px] sm:h-[70vh] text-muted-foreground bg-muted/30 rounded-lg">
      <FileText className="h-10 w-10 mb-2 opacity-40" />
      <span className="text-sm">{message}</span>
    </div>
  )
}

function LoadingPreview(): ReactElement {
  return (
    <div className="flex items-center justify-center h-[300px] sm:h-[70vh] bg-muted/30 rounded-lg">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  )
}

/**
 * Preview a local File via object URL. Re-derives the URL whenever the
 * `file` prop changes so queue-driven switches update the iframe in place.
 */
function LocalFilePreview({ file }: { file: File }): ReactElement {
  const url = useMemo(() => URL.createObjectURL(file), [file])
  const mimeType = resolveType(file)

  useEffect(() => {
    return () => URL.revokeObjectURL(url)
  }, [url])

  return <PreviewRenderer url={url} mimeType={mimeType} />
}

/** Fetch remote file and preview (edit dialog). */
function RemoteFilePreview({
  invoiceUuid,
  originalFilename,
}: {
  invoiceUuid: string
  originalFilename?: string | null
}): ReactElement {
  const [data, setData] = useState<{ url: string; mimeType: string } | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | undefined

    downloadInvoiceFile(invoiceUuid)
      .then(blob => {
        if (cancelled) return
        // Fall back to filename extension when Content-Type is generic
        let mimeType = blob.type
        if ((!mimeType || mimeType === 'application/octet-stream') && originalFilename) {
          mimeType = typeFromExtension(originalFilename)
        }
        // Re-wrap blob with correct type so the blob URL serves the right Content-Type
        // (browsers won't render <img> from a blob URL with application/octet-stream)
        const typedBlob = mimeType !== blob.type ? new Blob([blob], { type: mimeType }) : blob
        objectUrl = URL.createObjectURL(typedBlob)
        setData({ url: objectUrl, mimeType })
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [invoiceUuid, originalFilename])

  if (!data && !failed) return <LoadingPreview />
  if (!data) return <EmptyPreview message="Failed to load preview" />
  return <PreviewRenderer url={data.url} mimeType={data.mimeType} />
}

export function InvoiceFilePreview({
  file,
  invoiceUuid,
  hasFile,
  originalFilename,
}: InvoiceFilePreviewProps): ReactElement {
  if (file) return <LocalFilePreview file={file} />
  if (invoiceUuid && hasFile) {
    return <RemoteFilePreview invoiceUuid={invoiceUuid} originalFilename={originalFilename ?? null} />
  }
  return <EmptyPreview />
}
